import { createContext, useCallback, useContext, useEffect, useMemo, useState, type PropsWithChildren } from "react";

import { usePodcasts, type Podcast } from "@/hooks/usePodcasts";
import { useAuth } from "@/contexts/AuthContext";

export type { Podcast } from "@/hooks/usePodcasts";

type PodcastContextValue = {
  selectedPodcast: Podcast | null;
  setSelectedPodcast: (podcast: Podcast) => void;
  availablePodcasts: Podcast[];
  isLoading: boolean;
  refreshPodcasts: () => void;
};

const PodcastContext = createContext<PodcastContextValue | undefined>(undefined);

export function PodcastProvider({ children }: PropsWithChildren) {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { podcasts, isLoading: podcastsLoading, refreshPodcasts } = usePodcasts();
  const [selectedPodcast, setSelectedPodcastState] = useState<Podcast | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Compute loading state: loading until auth is done and podcasts are fetched
  const isLoading = authLoading || (isAuthenticated && podcastsLoading);

  // Initialize selected podcast once podcasts are loaded
  useEffect(() => {
    if (authLoading || podcastsLoading || hasInitialized) return;

    if (!isAuthenticated) {
      setSelectedPodcastState(null);
      setHasInitialized(true);
      return;
    }

    if (podcasts.length === 0) {
      setSelectedPodcastState(null);
      setHasInitialized(true);
      return;
    }

    const storageKey = user ? `selectedPodcast:${user.id}` : "selectedPodcast";
    const storedPodcastId = typeof window !== "undefined" ? window.localStorage.getItem(storageKey) : null;

    const preferred = storedPodcastId ? podcasts.find(podcast => podcast.id === storedPodcastId) : undefined;
    const nextPodcast = preferred ?? podcasts[Math.floor(Math.random() * podcasts.length)];

    setSelectedPodcastState(nextPodcast ?? null);
    setHasInitialized(true);
  }, [authLoading, podcastsLoading, isAuthenticated, podcasts, user, hasInitialized]);

  // Reset when user logs out
  useEffect(() => {
    if (!isAuthenticated && hasInitialized) {
      setSelectedPodcastState(null);
      setHasInitialized(false);
    }
  }, [isAuthenticated, hasInitialized]);

  const setSelectedPodcast = useCallback((podcast: Podcast) => {
    setSelectedPodcastState(podcast);
    const storageKey = user ? `selectedPodcast:${user.id}` : "selectedPodcast";
    if (typeof window !== "undefined") {
      window.localStorage.setItem(storageKey, podcast.id);
    }
  }, [user]);

  const value = useMemo<PodcastContextValue>(
    () => ({
      selectedPodcast,
      setSelectedPodcast,
      availablePodcasts: isAuthenticated ? podcasts : [],
      isLoading,
      refreshPodcasts,
    }),
    [podcasts, isLoading, selectedPodcast, setSelectedPodcast, isAuthenticated, refreshPodcasts],
  );

  return <PodcastContext.Provider value={value}>{children}</PodcastContext.Provider>;
}

export function usePodcast() {
  const context = useContext(PodcastContext);
  if (!context) {
    throw new Error("usePodcast must be used within a PodcastProvider");
  }
  return context;
}
